'use client';
import { useState } from 'react';
import Slider from 'react-slick';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { LeftArrow, RightArrow } from '@repo/ui/src/components/Carousels/CarouselArrows';
import FeatureCarouselCard, { FeatureCardProps } from './_FeatureCarouselCard';
import { BackgroundStyleEnum } from '@repo/ui/src/enums/shared-enums';
import Section from '@repo/ui/src/components/Section';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '@repo/ui/src/utils/appConfig';
import styles from './FeatureCarousel.module.css';

interface FeatureCarouselProps {
  cards: Array<FeatureCardProps>;
  hideNavigationTabs?: boolean;
  background: string;
  lightArrows?: boolean;
  _testID?: string;
}

const FeatureCarousel = ({
  cards,
  hideNavigationTabs,
  background,
  _testID,
}: FeatureCarouselProps) => {
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const isMeater = appConfig.isMeater;

  const topSettings = {
    accessibility: true,
    dots: true,
    infinite: true,
    centerMode: true,
    centerPadding: isMeater ? '0px' : '180px',
    speed: reduceMotion ? 0 : 500,
    slidesToShow: 1,
    arrows: !isMeater,
    slidesToScroll: 1,
    focusOnSelect: true,
    afterChange: (current: number) => setSelectedCardIndex(current),
    nextArrow: <RightArrow onImage aria-label="Next slide" role="button" arrowSize="40" />,
    prevArrow: <LeftArrow onImage aria-label="Previous slide" role="button" arrowSize="40" />,
    responsive: [
      {
        breakpoint: 960,
        settings: {
          centerPadding: isMeater ? '0px' : '100px',
          arrows: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          centerPadding: isMeater ? '0px' : '25px',
          arrows: false,
        },
      },
    ],
    appendDots: (dots: boolean) => (
      <div>{(isMobile || !hideNavigationTabs || isMeater) && <ul> {dots} </ul>}</div>
    ),
    customPaging: (i: number) => (
      <button
        aria-current={selectedCardIndex === i}
        aria-label={`Go to slide ${i + 1} of ${cards.length}: ${cards[i].toutContent?.heading || 'Slide content'}`}
        onClick={() => setSelectedCardIndex(i)}
        className={classNames('flex flex-col items-center w-full gap-2')}
      >
        {/* Divider bar */}
        <div
          className={classNames(
            'w-full transition-colors',
            isMeater ? 'h-[3px]' : 'h-1 my-6',
            isMeater ? (selectedCardIndex === i ? 'bg-[#c8102e]' : 'bg-[#808285]') : 'bg-brand-300',
          )}
        />
        {/* Label */}
        <span
          className={classNames(
            'uppercase text-center',
            isMeater
              ? 'font-display font-bold text-sm leading-none transition-colors'
              : 'tra-text-web-heading-h6 text-brand-300',
            isMeater && (selectedCardIndex === i ? 'text-[#1d1d1f]' : 'text-[#808285]'),
          )}
        >
          {cards[i].toutContent?.heading}
        </span>
        {/* Screen reader helper */}
        <span className="sr-only">
          {cards[i].toutContent?.heading || `Slide ${i + 1} content`}
        </span>
      </button>
    ),
  };

  return (
    <div
      role="region"
      aria-label={`feature carousel ${_testID ? _testID : ''}`}
      data-test-id={`feature_carousel_${_testID}`}
      className={classNames(
        'feature-carousel relative',
        isMeater ? styles.meaterCarousel : styles.traegerCarousel,
      )}
    >
      <span className="sr-only">
        This is a carousel with {cards.length} slides. Use the previous and next buttons to navigate
        between slides, or use the tab key to access the slide navigation tabs. Each slide can be
        selected directly using the tabs below the carousel.
      </span>
      <Section
        className="w-full overflow-hidden"
        white={isMeater || !background || background === BackgroundStyleEnum.White}
        dark={!isMeater && background === BackgroundStyleEnum.TextureDark}
        black={!isMeater && background === BackgroundStyleEnum.Black}
        darkGray={!isMeater && background === BackgroundStyleEnum.DarkGray}
        texture={
          !isMeater &&
          (background === BackgroundStyleEnum.TextureLight ||
            background === BackgroundStyleEnum.TextureDark)
        }
        gutterY
      >
        <Slider {...topSettings}>
          {cards.map((card, i) => (
            <FeatureCarouselCard
              key={card.toutContent?.heading || i}
              {...card}
              index={i}
              totalSlides={cards.length}
            />
          ))}
        </Slider>
      </Section>
    </div>
  );
};

export default FeatureCarousel;
